import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, Check, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { ipdApi, type IPDBillEditRequest } from '@/api/ipd';
import { toast } from 'sonner';

export default function IPDBillEditRequestsBell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reviewNote, setReviewNote] = useState('');
  const [actingId, setActingId] = useState<string | null>(null);

  const { data: requests = [], isLoading } = useQuery({
    queryKey: ['ipd-bill-edit-requests', 'PENDING'],
    queryFn: async () => {
      const res = await ipdApi.listBillEditRequests({ status: 'PENDING' });
      return (res.data?.data ?? []) as IPDBillEditRequest[];
    },
    refetchInterval: 60_000,
  });

  const count = requests.length;

  const approveMut = useMutation({
    mutationFn: ({ id, adminNote }: { id: string; adminNote?: string }) =>
      ipdApi.approveBillEditRequest(id, adminNote),
    onSuccess: () => {
      toast.success('Edit request approved — receptionist can modify the bill for 48 hours');
      setReviewNote('');
      setActingId(null);
      queryClient.invalidateQueries({ queryKey: ['ipd-bill-edit-requests'] });
    },
    onError: (e: { response?: { data?: { message?: string } } }) =>
      toast.error(e.response?.data?.message || 'Failed to approve'),
  });

  const rejectMut = useMutation({
    mutationFn: ({ id, adminNote }: { id: string; adminNote?: string }) =>
      ipdApi.rejectBillEditRequest(id, adminNote),
    onSuccess: () => {
      toast.success('Edit request rejected');
      setReviewNote('');
      setActingId(null);
      queryClient.invalidateQueries({ queryKey: ['ipd-bill-edit-requests'] });
    },
    onError: (e: { response?: { data?: { message?: string } } }) =>
      toast.error(e.response?.data?.message || 'Failed to reject'),
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative text-white hover:bg-white/10"
          aria-label="IPD bill edit requests"
        >
          <Bell className="h-5 w-5" />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center">
              {count > 9 ? '9+' : count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <div className="p-3 border-b font-medium text-sm">IPD bill edit requests</div>
        <div className="max-h-96 overflow-y-auto">
          {isLoading ? (
            <p className="p-3 text-sm text-muted-foreground">Loading…</p>
          ) : requests.length === 0 ? (
            <p className="p-3 text-sm text-muted-foreground">No pending requests.</p>
          ) : (
            <ul className="divide-y">
              {requests.map((r) => {
                const patient = r.admission?.queue?.patient;
                const ipdNum = r.admission?.queue?.ipdNumber ?? '—';
                return (
                  <li key={r.id} className="p-3 space-y-2">
                    <div className="text-sm font-medium">{patient?.name ?? 'Patient'}</div>
                    <div className="text-xs text-muted-foreground">
                      IPD {ipdNum} · {patient?.uhid} · Bill ₹
                      {Number(r.ipdBill?.netAmount ?? 0).toFixed(2)}
                    </div>
                    <p className="text-xs text-gray-700">{r.reason}</p>
                    <p className="text-xs text-muted-foreground">
                      By {r.requestedBy?.name ?? 'Receptionist'} ·{' '}
                      {new Date(r.createdAt).toLocaleString('en-IN')}
                    </p>
                    {actingId === r.id && (
                      <Textarea
                        placeholder="Optional note to receptionist"
                        value={reviewNote}
                        onChange={(e) => setReviewNote(e.target.value)}
                        rows={2}
                        className="text-xs"
                      />
                    )}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="flex-1 h-8"
                        disabled={approveMut.isPending || rejectMut.isPending}
                        onClick={() => {
                          if (actingId !== r.id) {
                            setActingId(r.id);
                            setReviewNote('');
                            return;
                          }
                          approveMut.mutate({ id: r.id, adminNote: reviewNote || undefined });
                        }}
                      >
                        <Check className="h-3 w-3 mr-1" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 h-8"
                        disabled={approveMut.isPending || rejectMut.isPending}
                        onClick={() => {
                          if (actingId !== r.id) {
                            setActingId(r.id);
                            setReviewNote('');
                            return;
                          }
                          rejectMut.mutate({ id: r.id, adminNote: reviewNote || undefined });
                        }}
                      >
                        <X className="h-3 w-3 mr-1" />
                        Reject
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 px-2"
                        onClick={() => {
                          setOpen(false);
                          navigate(
                            `/hospital-admin/ipd/${r.admissionId}/billing?billTrack=REVENUE`
                          );
                        }}
                      >
                        Bill
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="p-2 border-t">
          <Button
            variant="ghost"
            className="w-full text-sm h-9"
            onClick={() => {
              setOpen(false);
              navigate('/hospital-admin/ipd-bill-edit-requests');
            }}
          >
            View all requests
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
